import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import useFleetStore from "../utils/store";
import Button from "./Common/Button";
import Checkbox from "./Common/Checkbox";
import PendingZoneBadge from "./Common/PendingZoneBadge";
import { getUnitZone } from "../utils/unitZoneUtils";
import { unitListReturnState } from "../utils/unitListReturn";
import "./Common/UnitsListViewComponent.css";

const buildLocationString = (metadata = {}) =>
  [
    metadata.propertyName,
    metadata.buildingName,
    metadata.unit ? `${metadata.unit}` : null,
    metadata.floor ? `Floor ${metadata.floor}` : null,
    metadata.room ? `Room ${metadata.room}` : null,
  ]
    .filter(Boolean)
    .join(", ");

const UnitActionsToggle = ({ serialNumber, disabled, onClick }) => (
  <Button
    data-testid={`unit-${serialNumber}-actions-toggle`}
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-2 text-primaryText font-semibold text-sm leading-6 tracking-[0.021px] ${
      disabled ? "opacity-50 cursor-not-allowed" : ""
    }`}
    aria-disabled={disabled}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
      <circle cx="5" cy="12" r="2" fill={disabled ? "#AFC3BC" : "#FF8C65"} />
      <circle cx="12" cy="12" r="2" fill={disabled ? "#AFC3BC" : "#FF8C65"} />
      <circle cx="19" cy="12" r="2" fill={disabled ? "#AFC3BC" : "#FF8C65"} />
    </svg>
  </Button>
);

const UnassociatedUnitsList = ({
  unit,
  handleDeleteClick,
  handleAddOrChangeZone,
  selectedUnitsForBulkAction,
  addUnitsToSelectedUnitsForBulkAction,
  removeUnitsFromSelectedUnitsForBulkAction,
  isActionsDisabled,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnToManageUnits = useMemo(
    () => unitListReturnState("manageunits", location.search),
    [location.search]
  );
  const setSelectedUnit = useFleetStore((state) => state.setSelectedUnit);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [fullPageSelectionState, setFullPageSelectionState] = useState("NOT_SELECTED"); // NOT_SELECTED, PARTIALLY_SELECTED, FULLY_SELECTED
  const dropdownRefs = useRef({});
  const [columnSizing, setColumnSizing] = useState(() => {
    try {
      const stored = localStorage.getItem("unassociatedUnitsListColumnWidths");
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      return {};
    }
  });

  const toggleDropdown = useCallback((e, id) => {
    e.stopPropagation();
    setOpenDropdownId((prevId) => (prevId === id ? null : id));
  }, []);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const ref = dropdownRefs.current[openDropdownId];
      if (ref && !ref.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  const openUnitDetails = useCallback((item) => {
    navigate(`/units/${item.serialNumber}`, {
      state: { ...returnToManageUnits, pendingZone: item.pendingZone || null },
    });
  }, [navigate, returnToManageUnits]);

  const handleButtonClick = useCallback((item) => {
    setSelectedUnit(item);
    navigate("/configureUnits", { state: returnToManageUnits });
  }, [navigate, returnToManageUnits, setSelectedUnit]);

  const handleEditClick = useCallback((e, item) => {
    e.preventDefault();
    setOpenDropdownId(null); // close before navigation
    navigate(`/EditUnit?unid=${item?.zone?.unid}&serialNumber=${item?.serialNumber}`, {
      state: returnToManageUnits,
    });
  }, [navigate, returnToManageUnits]);

  const handleDeleteClickWrapper = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdownId(null); // close before triggering delete
    handleDeleteClick(e, item);
  }, [handleDeleteClick]);

  const handleFullPageBulkActionCheckboxClick = useCallback((checked) => {
    if (checked) {
      addUnitsToSelectedUnitsForBulkAction(unit.filter((item) => item.metadata?.propertyUnid));
    } else {
      removeUnitsFromSelectedUnitsForBulkAction(unit);
    }
  }, [addUnitsToSelectedUnitsForBulkAction, removeUnitsFromSelectedUnitsForBulkAction, unit]);

  useEffect(() => {
    const selectableUnits = unit.filter((item) => item.metadata?.propertyUnid);
    const selectedCount = selectableUnits.filter((item) => !!selectedUnitsForBulkAction[item.serialNumber]).length;
    if (selectableUnits.length === selectedCount && selectedCount > 0) {
      setFullPageSelectionState("FULLY_SELECTED");
    } else if (selectedCount === 0) {
      setFullPageSelectionState("NOT_SELECTED");
    } else if (fullPageSelectionState === "FULLY_SELECTED") {
      setFullPageSelectionState("PARTIALLY_SELECTED");
    }
  }, [unit, fullPageSelectionState, selectedUnitsForBulkAction, setFullPageSelectionState]);

  const rows = useMemo(
    () =>
      unit.map((item, idx) => ({
        ...item,
        rowIndex: idx,
        locationString: buildLocationString(item?.metadata || {}),
      })),
    [unit]
  );

  const baseColumns = useMemo(
    () => [
      {
        id: "bulkSelect",
        header: () => (
          <Checkbox
            data-testid="unassociated-units-select-all-checkbox"
            checked={fullPageSelectionState === "FULLY_SELECTED"}
            indeterminate={fullPageSelectionState === "PARTIALLY_SELECTED"}
            onChange={handleFullPageBulkActionCheckboxClick}
          />
        ),
        size: 75,
        enableResizing: false,
        cell: ({ row }) =>
          row.original?.metadata?.propertyUnid ? (
            <Checkbox
              data-testid={`unit-${row.original.serialNumber}-checkbox`}
              checked={!!selectedUnitsForBulkAction[row.original.serialNumber]}
              onChange={(checked) => {
                if (checked) {
                  addUnitsToSelectedUnitsForBulkAction(row.original);
                } else {
                  removeUnitsFromSelectedUnitsForBulkAction(row.original);
                }
              }}
            />
          ) : null,
      },
      {
        id: "serialNumber",
        accessorKey: "serialNumber",
        header: "Serial Number",
        size: 180,
        cell: ({ row }) => (
          <span
            data-testid={`unit-${row.original.serialNumber}-name`}
            className={`text-sm lg:text-base cursor-pointer hover:underline ${row.original?.activated ? "" : "opacity-50"}`}
            onClick={() => openUnitDetails(row.original)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openUnitDetails(row.original);
              }
            }}
            role="button"
            tabIndex={0}
          >
            {row.original.serialNumber}
          </span>
        ),
      },
      {
        id: "fleetName",
        accessorKey: "fleetName",
        header: "Zone",
        size: 220,
        cell: ({ row }) => {
          const zone = getUnitZone(row.original);
          if (!zone) return <p className="truncate max-w-60">--</p>;
          return (
            <div className="flex items-center gap-2 min-w-0">
              <p className="truncate max-w-52">{zone.name}</p>
              {!row.original?.zone && (
                <PendingZoneBadge
                  data-testid={`unit-${row.original.serialNumber}-pending-zone-badge`}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "locationString",
        id: "location",
        header: "Location",
        size: 260,
        cell: ({ row }) => (
          <p className="line-clamp-2 text-ellipsis break-words" title={row.original.locationString}>
            {row.original.locationString || "--"}
          </p>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        size: 140,
        cell: ({ row }) => <span>{row.original?.activated ? "Active" : "Inactive"}</span>,
      },
      {
        id: "actions",
        header: null,
        size: 150,
        meta: { cellClassName: "allow-overflow" },
        cell: ({ row }) => (
          <div className="max-w-[80%] whitespace-break-spaces">
            {!row.original?.zone ? (
              !row.original?.metadata?.propertyUnid ? (
                <Button
                  data-testid={`unit-${row.original.serialNumber}-configure-button`}
                  className="text-[#FF8C65] text-base cursor-pointer leading-4 font-semibold font-sans"
                  onClick={() => handleButtonClick(row.original)}
                >
                  Configure
                </Button>
              ) : (
                <div
                  className="dropdownHolder relative"
                  ref={(el) => (dropdownRefs.current[row.original.serialNumber] = el)}
                >
                  <UnitActionsToggle
                    serialNumber={row.original.serialNumber}
                    disabled={isActionsDisabled}
                    onClick={(e) => {
                      if (isActionsDisabled) return;
                      toggleDropdown(e, row.original.serialNumber);
                    }}
                  />

                  {!isActionsDisabled && openDropdownId === row.original.serialNumber && (
                    <div
                      className={`dropdownContent min-w-24 text-nowrap absolute left-2.5 bg-[#FFF4D1] rounded-lg shadow-appShadow block z-50 translate-y-2
                        ${row.original.rowIndex < 5 ? "top-full" : "bottom-full"}`}
                      data-testid={`unit-${row.original.serialNumber}-actions-menu`}
                    >
                      <ul className="flex flex-col">
                        {row.original?.metadata?.propertyUnid && (
                          <li
                            data-testid={`unit-${row.original.serialNumber}-change-zone`}
                            className="text-dark-grey font-medium text-sm leading-6 cursor-pointer px-3 py-2 rounded border-b border-grey"
                            onClick={(e) => handleAddOrChangeZone(e, row.original)}
                          >
                            {getUnitZone(row.original) ? "Change Zone" : "Add to Zone"}
                          </li>
                        )}
                        <li
                          data-testid={`unit-${row.original.serialNumber}-edit`}
                          className="text-dark-grey font-medium text-sm leading-6 cursor-pointer px-3 py-2 rounded"
                          onClick={() => handleButtonClick(row.original)}
                        >
                          Edit Unit
                        </li>
                        {getUnitZone(row.original) && (
                          <li
                            data-testid={`unit-${row.original.serialNumber}-remove-from-zone`}
                            className="text-dark-grey font-medium text-sm leading-6 cursor-pointer px-3 py-2 rounded border-t border-grey"
                            onClick={(e) => handleDeleteClickWrapper(e, row.original)}
                          >
                            Remove from zone
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div
                className="dropdownHolder relative"
                ref={(el) => (dropdownRefs.current[row.original.serialNumber] = el)}
              >
                <UnitActionsToggle
                  serialNumber={row.original.serialNumber}
                  disabled={isActionsDisabled}
                  onClick={(e) => {
                    if (isActionsDisabled) return;
                    toggleDropdown(e, row.original.serialNumber);
                  }}
                />

                {!isActionsDisabled && openDropdownId === row.original.serialNumber && (
                  <div
                    className={`dropdownContent min-w-28 text-nowrap absolute bg-[#FFF4D1] rounded-lg shadow-appShadow block z-50 translate-y-2
                      ${row.original.rowIndex < 5 ? "top-full right-[80%]" : "bottom-full right-[80%]"}`}
                    data-testid={`unit-${row.original.serialNumber}-actions-menu`}
                  >
                    <ul className="flex flex-col">
                      {row.original?.metadata?.propertyUnid && (
                        <li
                          data-testid={`unit-${row.original.serialNumber}-change-zone`}
                          className="text-dark-grey font-medium text-sm leading-6 cursor-pointer px-3 py-2 rounded border-b border-grey"
                          onClick={(e) => handleAddOrChangeZone(e, row.original)}
                        >
                          Change Zone
                        </li>
                      )}
                      <li
                        data-testid={`unit-${row.original.serialNumber}-edit`}
                        className="text-dark-grey font-medium text-sm leading-6 cursor-pointer px-3 py-2 rounded border-b border-grey"
                        onClick={(e) => handleEditClick(e, row.original)}
                      >
                        Edit Unit
                      </li>
                      <li
                        data-testid={`unit-${row.original.serialNumber}-remove-from-zone`}
                        className="text-dark-grey font-medium text-sm leading-6 cursor-pointer px-3 py-2 rounded"
                        onClick={(e) => handleDeleteClickWrapper(e, row.original)}
                      >
                        Remove from zone
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ),
      },
    ],
    [
      addUnitsToSelectedUnitsForBulkAction,
      fullPageSelectionState,
      handleAddOrChangeZone,
      handleDeleteClickWrapper,
      handleEditClick,
      handleFullPageBulkActionCheckboxClick,
      handleButtonClick,
      removeUnitsFromSelectedUnitsForBulkAction,
      selectedUnitsForBulkAction,
      toggleDropdown,
      openDropdownId,
      isActionsDisabled,
      openUnitDetails,
    ]
  );

  const handleColumnSizingChange = useCallback((updater) => {
    setColumnSizing((prev) => {
      const updated = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem("unassociatedUnitsListColumnWidths", JSON.stringify(updated));
      } catch (error) {
        // ignore write failures
      }
      return updated;
    });
  }, []);

  const gridHeight = useMemo(
    () => Math.max(320, Math.min(900, (rows.length || 1) * 76 + 64)),
    [rows.length]
  );

  const table = useReactTable({
    data: rows,
    columns: baseColumns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnSizing },
    onColumnSizingChange: handleColumnSizingChange,
    columnResizeMode: "onChange",
    getRowId: (row) => row.serialNumber,
  });

  return (
    <div className="mt-4 unitsListHolder rounded-md scrollVisible overflow-x-auto w-full" data-testid="unassociated-units-list-view">
      <div className="units-data-grid" style={{ height: gridHeight, width: "100%" }}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} style={{ height: 54 }}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="units-table-header-cell"
                    style={{ width: header.getSize() }}
                  >
                    <div className="units-table-header-content">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                    {header.column.getCanResize() && (
                      <div
                        className={`column-resizer ${header.column.getIsResizing() ? "is-resizing" : ""}`}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          header.getResizeHandler()(event);
                        }}
                        onTouchStart={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          header.getResizeHandler()(event);
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="units-table-row"
                style={{ height: 76 }}
                data-testid={`unit-row-${row.original.serialNumber}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`units-table-cell ${cell.column.columnDef.meta?.cellClassName || ""}`}
                    data-testid={`unit-${row.original.serialNumber}-cell-${cell.column.id}`}
                    style={{ width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UnassociatedUnitsList;
